import React, { useState, useEffect, useCallback } from 'react';
import { 
  User, 
  Search, 
  Trash2,
  UserPlus,
  Shield,
  ShieldCheck,
  Mail,
  Lock,
  X,
  Check,
  AlertCircle,
  Edit2
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, 
  getAuth
} from 'firebase/auth';
import { 
  initializeApp, 
  deleteApp 
} from 'firebase/app';
import { db, firebaseConfig } from '../../firebase/config';
import { getCoachingAdminPermissions } from '../../firebase/collections';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const ALL_SECTIONS = [
  { id: 'dashboard', name: 'Dashboard' },
  { id: 'admissions', name: 'Admissions' },
  { id: 'tests', name: 'Test Management' },
  { id: 'exams', name: 'Exam Management' },
  { id: 'resources', name: 'Resource Management' },
  { id: 'batches', name: 'Batch Management' },
  { id: 'students', name: 'Student Management' },
  { id: 'teachers', name: 'Teacher Management' },
  { id: 'settlements', name: 'Teacher Settlements' },
  { id: 'attendance', name: 'Attendance' },
  { id: 'transport', name: 'Transport Management' },
  { id: 'activities', name: 'Activities Management' },
  { id: 'grievance', name: 'Grievance Management' },
  { id: 'leave', name: 'Leave Management' },
  { id: 'fees', name: 'Fee Management' },
  { id: 'report-cards', name: 'Report Cards' },
  { id: 'analytics', name: 'Data Analytics' },
  { id: 'id-cards', name: 'ID Cards' },
  { id: 'store', name: 'Store Management' },
  { id: 'staff', name: 'Staff Management' },
  { id: 'subscription', name: 'Subscription' },
];

const CoachingStaffManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [allowedSections, setAllowedSections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [addingStaff, setAddingStaff] = useState(false);
  const [updatingStaff, setUpdatingStaff] = useState(false);
  const [newStaff, setNewStaff] = useState({
    email: '',
    password: '',
    displayName: '',
    accessibleSections: ['dashboard']
  });

  const coachingId = userProfile?.coachingId;

  // Load coaching admin's own permissions
  useEffect(() => {
    const loadAdminPermissions = async () => {
      if (!currentUser?.email) {
        setLoadingPermissions(false);
        return;
      }

      // Check if user is founder, superadmin, or regular admin - they have full access
      const isFounder = userProfile?.role === 'founder' || currentUser.email.includes('founder');
      const isSuperAdmin = userProfile?.role === 'superadmin';
      const isRegularAdmin = userProfile?.role === 'admin';
      
      if (isFounder || isSuperAdmin || isRegularAdmin) {
        setAllowedSections(ALL_SECTIONS.map(s => s.id)); // All sections allowed
        setLoadingPermissions(false);
        return;
      }

      try {
        const result = await getCoachingAdminPermissions(currentUser.email);
        if (result.success && result.data && result.data.permissions && result.data.permissions.length > 0) {
          // Has specific permissions set
          setAllowedSections(result.data.permissions);
        } else {
          // No permissions document OR empty permissions array = full access
          setAllowedSections(ALL_SECTIONS.map(s => s.id));
        }
      } catch (error) {
        console.error('Error loading admin permissions:', error);
        setAllowedSections(ALL_SECTIONS.map(s => s.id)); // Default to full access on error
      } finally {
        setLoadingPermissions(false);
      }
    };

    loadAdminPermissions();
  }, [currentUser?.email, userProfile?.role]);

  // Filter sections based on what coaching admin has access to
  const SECTIONS = ALL_SECTIONS.filter(section => allowedSections.includes(section.id));

  const loadStaff = useCallback(async () => {
    if (!coachingId) return;
    try {
      setLoading(true);
      const staffQuery = query(
        collection(db, 'users'),
        where('role', '==', 'coaching_staff'),
        where('coachingId', '==', coachingId)
      );
      
      const snapshot = await getDocs(staffQuery);
      const staffList = [];
      snapshot.forEach((doc) => {
        staffList.push({ id: doc.id, ...doc.data() });
      });
      setStaff(staffList);
    } catch (error) {
      console.error('Error loading staff:', error);
      toast.error('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  }, [coachingId]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!newStaff.email || !newStaff.password || !newStaff.displayName) {
      toast.error('Please fill all fields');
      return;
    }

    setAddingStaff(true);
    let secondaryApp = null;

    try {
      // 1. Create user in Firebase Auth using a secondary app instance
      // This prevents the current admin from being signed out
      const secondaryAppName = `StaffCreation-${Date.now()}`;
      secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);
      
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth, 
        newStaff.email, 
        newStaff.password
      );
      
      const uid = userCredential.user.uid;

      // 2. Create user profile in Firestore
      const staffData = {
        uid,
        email: newStaff.email.toLowerCase(),
        displayName: newStaff.displayName,
        role: 'coaching_staff',
        coachingId: coachingId,
        coachingName: userProfile?.coachingName || '',
        accessibleSections: newStaff.accessibleSections,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        isActive: true
      };

      await setDoc(doc(db, 'users', uid), staffData);

      toast.success('Staff member added successfully');
      setShowAddModal(false);
      setNewStaff({
        email: '',
        password: '',
        displayName: '',
        accessibleSections: ['dashboard']
      });
      loadStaff();
    } catch (error) {
      console.error('Error adding staff:', error);
      toast.error(error.message || 'Failed to add staff member');
    } finally {
      setAddingStaff(false);
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
        } catch (err) {
          console.error('Error deleting secondary app:', err);
        }
      }
    }
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    if (!editingStaff || !editingStaff.displayName) {
      toast.error('Please provide a name');
      return;
    }

    setUpdatingStaff(true);
    try {
      await updateDoc(doc(db, 'users', editingStaff.id), {
        displayName: editingStaff.displayName,
        accessibleSections: editingStaff.accessibleSections,
        updatedAt: serverTimestamp()
      });
      toast.success('Staff permissions updated');
      setEditingStaff(null);
      loadStaff();
    } catch (error) {
      toast.error('Failed to update staff member');
    } finally {
      setUpdatingStaff(false);
    }
  };

  const toggleSection = (sectionId) => {
    if (editingStaff) {
      setEditingStaff(prev => ({
        ...prev,
        accessibleSections: prev.accessibleSections.includes(sectionId)
          ? prev.accessibleSections.filter(id => id !== sectionId)
          : [...prev.accessibleSections, sectionId]
      }));
    } else {
      setNewStaff(prev => ({
        ...prev,
        accessibleSections: prev.accessibleSections.includes(sectionId)
          ? prev.accessibleSections.filter(id => id !== sectionId)
          : [...prev.accessibleSections, sectionId]
      }));
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to remove this staff member? This will NOT delete their authentication account, only their profile and access.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', staffId));
      toast.success('Staff member removed');
      loadStaff();
    } catch (error) {
      toast.error('Failed to remove staff member');
    }
  };

  const handleToggleAccess = async (staffMember, sectionId) => {
    const updatedSections = staffMember.accessibleSections.includes(sectionId)
      ? staffMember.accessibleSections.filter(id => id !== sectionId)
      : [...staffMember.accessibleSections, sectionId];

    try {
      await updateDoc(doc(db, 'users', staffMember.id), {
        accessibleSections: updatedSections,
        updatedAt: serverTimestamp()
      });
      loadStaff();
      toast.success('Permissions updated');
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const filteredStaff = staff.filter(s => 
    s.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Staff Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage coaching staff and their access permissions</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <UserPlus size={20} />
          <span>Add Staff Member</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search staff by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
        />
      </div>

      {/* Staff List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loadingPermissions ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading permissions...</p>
          </div>
        ) : loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading staff members...</p>
          </div>
        ) : filteredStaff.length === 0 ? (
          <div className="p-12 text-center">
            <User className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 dark:text-gray-400">No staff members found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Staff Member</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Permissions</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredStaff.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 font-bold">
                          {s.displayName?.[0]?.toUpperCase() || <User size={20} />}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{s.displayName}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 uppercase">
                        Staff
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {s.accessibleSections?.map(sectionId => (
                          <span 
                            key={sectionId}
                            className="px-2 py-0.5 text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                          >
                            {ALL_SECTIONS.find(sec => sec.id === sectionId)?.name || sectionId}
                          </span>
                        ))}
                        {(!s.accessibleSections || s.accessibleSections.length === 0) && (
                          <span className="text-xs text-red-500 italic">No access</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingStaff(s)}
                          className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                          title="Edit Staff"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteStaff(s.id)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Remove Staff"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <UserPlus className="mr-2 text-primary-600" size={24} />
                Add New Staff Member
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddStaff} className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      required
                      value={newStaff.displayName}
                      onChange={(e) => setNewStaff(prev => ({ ...prev, displayName: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      required
                      value={newStaff.email}
                      onChange={(e) => setNewStaff(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="staff@example.com"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={newStaff.password}
                      onChange={(e) => setNewStaff(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <Shield size={18} className="mr-2 text-primary-600" />
                  Section Access Permissions
                </label>
                {SECTIONS.length === 0 ? (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg flex items-start space-x-3">
                    <AlertCircle className="text-yellow-600 dark:text-yellow-400 mt-0.5" size={20} />
                    <div className="text-sm text-yellow-700 dark:text-yellow-300">
                      <p className="font-semibold mb-1">No Sections Available</p>
                      <p>You don't have access to any sections. Contact your administrator to grant you permissions.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-all ${
                        newStaff.accessibleSections.includes(section.id)
                          ? 'bg-primary-50 border-primary-500 text-primary-700 dark:bg-primary-900/20 dark:border-primary-500 dark:text-primary-400'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
                      }`}
                    >
                      <span>{section.name}</span>
                      {newStaff.accessibleSections.includes(section.id) ? (
                        <Check size={14} className="ml-2" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>Note:</strong> You can only grant access to sections that you have permission to access ({allowedSections.length} section{allowedSections.length !== 1 ? 's' : ''}).
                  </p>
                </div>
              </>
            )}
          </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg flex items-start space-x-3 mb-6">
                <AlertCircle className="text-blue-600 dark:text-blue-400 mt-0.5" size={20} />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p className="font-semibold mb-1">Important Note:</p>
                  <p>Adding a staff member will automatically create their authentication account. They can login immediately using the email and password provided above.</p>
                </div>
              </div>

              <div className="flex space-x-4 sticky bottom-0 bg-white dark:bg-gray-800 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingStaff}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {addingStaff ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Creating Account...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={20} />
                      <span>Create Staff Account</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {editingStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <Edit2 className="mr-2 text-primary-600" size={24} />
                Edit Staff Permissions
              </h2>
              <button onClick={() => setEditingStaff(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdateStaff} className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      required
                      value={editingStaff.displayName}
                      onChange={(e) => setEditingStaff(prev => ({ ...prev, displayName: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address (Cannot be changed)
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      disabled
                      value={editingStaff.email}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 cursor-not-allowed outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <Shield size={18} className="mr-2 text-primary-600" />
                  Update Access Permissions
                </label>
                {SECTIONS.length === 0 ? (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg flex items-start space-x-3">
                    <AlertCircle className="text-yellow-600 dark:text-yellow-400 mt-0.5" size={20} />
                    <div className="text-sm text-yellow-700 dark:text-yellow-300">
                      <p className="font-semibold mb-1">No Sections Available</p>
                      <p>You don't have access to any sections. Contact your administrator to grant you permissions.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-all ${
                        editingStaff.accessibleSections?.includes(section.id)
                          ? 'bg-primary-50 border-primary-500 text-primary-700 dark:bg-primary-900/20 dark:border-primary-500 dark:text-primary-400'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
                      }`}
                    >
                      <span>{section.name}</span>
                      {editingStaff.accessibleSections?.includes(section.id) ? (
                        <Check size={14} className="ml-2" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>Note:</strong> You can only grant access to sections that you have permission to access ({allowedSections.length} section{allowedSections.length !== 1 ? 's' : ''}).
                  </p>
                </div>
              </>
            )}
          </div>

              <div className="flex space-x-4 sticky bottom-0 bg-white dark:bg-gray-800 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingStaff(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updatingStaff}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {updatingStaff ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={20} />
                      <span>Update Permissions</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachingStaffManagement;
